<template>
    <div class="ds-range-slider" :class="{ error: (parsedError && !isArray(parsedError)), disabled }">
        <div class="range-info">
            <div
                ref="rangeStartInfoRef"
                class="range-info-start"
                :class="{ error: isArray(parsedError) ? parsedError[0] : null }"
                :style="`left: ${positionTooltip(currentValues.min)}`"
            >
                {{ currentValues.min }}
            </div>
            <div
                v-if="double"
                ref="rangeEndInfoRef"
                class="range-info-end"
                :class="{ error: isArray(parsedError) ? parsedError[1] : null }"
                :style="`left: ${positionTooltip(currentValues.max)}`"
            >
                {{ currentValues.max }}
            </div>
        </div>
        <div class="track" />
        <input
            ref="rangeStartRef"
            class="range-start"
            type="range"
            :min="min"
            :max="max"
            :step="step"
            :value="currentValues.min"
            :disabled="disabled"
            role="slider"
            tabindex="0"
            :aria-valuemin="min"
            :aria-valuenow="minValue"
            :aria-valuemax="max"
            @input="setValue"
            @change="updateImmediately ? null : emitValue(Number($event.target.value))"
        >
        <input
            v-if="double"
            ref="rangeEndRef"
            type="range"
            class="range-end"
            :min="min"
            :max="max"
            :step="step"
            :value="currentValues.max"
            :disabled="disabled"
            role="slider"
            tabindex="0"
            :aria-valuemin="min"
            :aria-valuenow="maxValue"
            :aria-valuemax="max"
            @input="setValue($event, false)"
            @change="updateImmediately ? null : emitValue(Number($event.target.value), false)"
        >
        <div
            v-if="error || helpText"
            class="text-after"
            :class="{
                'help-text': helpText,
                'error': error,
            }"
        >
            {{ error
                ? isArray(parsedError)
                    ? parsedError.find(i => !!i)
                    : error
                : helpText }}
        </div>
    </div>
</template>

<script setup>
import { ref, computed, watch, nextTick } from '#imports';

const props = defineProps({
    double: {
        type: Boolean,
        default: false,
    },
    gap: {
        type: Number,
        default: props => props.double ? 1 : 0,
    },
    min: {
        type: Number,
        default: 0,
    },
    max: {
        type: Number,
        default: 100,
    },
    step: {
        type: Number,
        default: 1,
    },
    minValue: {
        type: Number,
        required: true,
    },
    maxValue: {
        type: Number,
        default: props => props.maxValue || props.max,
    },
    disabled: {
        type: Boolean,
        default: false,
    },
    error: {
        type: String,
        default: null,
    },
    helpText: {
        type: String,
        default: null,
    },
    updateImmediately: {
        type: Boolean,
        default: false,
    },
});

const rangeStartRef = ref(null);
const rangeEndRef = ref(null);
const rangeStartInfoRef = ref(null);
const rangeEndInfoRef = ref(null);
const currentValues = ref({ min: props.minValue, max: props.maxValue });

const emit = defineEmits(['update:min-value', 'update:max-value']);

const positionTooltip = value => `${(value - props.min) / (props.max - props.min) * 100}%`;

const emitValue = (value, rangeStart = true) => emit(rangeStart ? 'update:min-value' : 'update:max-value', value);

const setValue = (e, rangeStart = true) => {
    let value = Number(e instanceof Event ? e.target.value : e);

    if (rangeStart) {
        if ((currentValues.value.max - value) <= props.gap) rangeStartRef.value.value = value = currentValues.value.max - props.gap;
        currentValues.value.min = value;
        if (props.updateImmediately) emitValue(value);
    } else {
        if ((value - currentValues.value.min) <= props.gap) rangeEndRef.value.value = value = currentValues.value.min + props.gap;
        currentValues.value.max = value;
        if (props.updateImmediately) emitValue(value, false);
    }
};

const parseJSON = str => {
    try {
        return JSON.parse(str);
    } catch (e) {
        return str;
    }
};
const isArray = v => v instanceof Array;
const parsedError = computed(() => parseJSON(props.error));

watch(() => props.step, async () => {
    await nextTick();

    setValue(rangeStartRef.value.value);
    if (props.double) setValue(rangeEndRef.value.value, false);
});
</script>

<style scoped>
.ds-range-slider {
  display: grid;
  grid-template-rows: 2rem auto;
}
.ds-range-slider input[type=range],
.ds-range-slider .track,
.ds-range-slider .track::before,
.ds-range-slider .track::after {
  grid-column: 1;
  grid-row: 2;
}
.ds-range-slider input[type=range] {
  appearance: none;
  background-color: transparent;
  pointer-events: none;
}
.ds-range-slider input[type=range]::-webkit-slider-thumb {
  appearance: none;
  background-color: var(--color-primary-700);
  border-radius: 50%;
  border: 2px solid var(--color-base-white-default);
  cursor: pointer;
  height: 1.5rem;
  pointer-events: auto;
  transition: 0.15s ease-in;
  width: 1.5rem;
}
.ds-range-slider input[type=range]::-webkit-slider-thumb:active {
  border: 2px solid var(--color-primary-400);
  transform: scale(1.2);
}
.ds-range-slider input[type=range]::-webkit-slider-thumb {
  box-shadow: none;
}
.ds-range-slider input[type=range]::-moz-range-thumb {
  appearance: none;
  background-color: var(--color-primary-700);
  border-radius: 50%;
  border: 2px solid var(--color-base-white-default);
  cursor: pointer;
  height: 1.5rem;
  pointer-events: auto;
  transition: 0.15s ease-in;
  width: 1.5rem;
}
.ds-range-slider input[type=range]::-moz-range-thumb:active {
  border: 2px solid var(--color-primary-400);
  transform: scale(1.2);
}
.ds-range-slider input[type=range]::-ms-thumb {
  appearance: none;
  background-color: var(--color-primary-700);
  border-radius: 50%;
  border: 2px solid var(--color-base-white-default);
  cursor: pointer;
  height: 1.5rem;
  pointer-events: auto;
  transition: 0.15s ease-in;
  width: 1.5rem;
}
.ds-range-slider input[type=range]::-ms-thumb:active {
  border: 2px solid var(--color-primary-400);
  transform: scale(1.2);
}
.ds-range-slider input[type=range]:disabled::-webkit-slider-thumb {
  background-color: var(--color-base-grey-200);
  cursor: not-allowed;
  opacity: 0.5;
}
.ds-range-slider input[type=range]:disabled::-webkit-slider-thumb:active {
  border: 0;
}
.ds-range-slider input[type=range]:disabled::-moz-range-thumb {
  background-color: var(--color-base-grey-200);
  cursor: not-allowed;
  opacity: 0.5;
}
.ds-range-slider input[type=range]:disabled::-moz-range-thumb:active {
  border: 0;
}
.ds-range-slider input[type=range]:disabled::-ms-thumb {
  background-color: var(--color-base-grey-200);
  cursor: not-allowed;
  opacity: 0.5;
}
.ds-range-slider input[type=range]:disabled::-ms-thumb:active {
  border: 0;
}
.ds-range-slider .track {
  display: grid;
  align-items: center;
  align-self: center;
  width: 100%;
  background: linear-gradient(to bottom, transparent calc(50% - 1px), var(--color-base-grey-200) calc(50% - 1px), var(--color-base-grey-200) 50%, transparent 50%);
}
.ds-range-slider .track::before, .ds-range-slider .track::after {
  background-color: var(--color-base-grey-200);
  border-radius: 0.75rem;
  content: "";
  height: 0.75rem;
  width: 0.75rem;
}
.ds-range-slider .track::after {
  justify-self: end;
}
.ds-range-slider .range-info {
  margin-inline: 0.75rem;
  position: relative;
}
.ds-range-slider .range-info > * {
  background-color: var(--color-base-white-default);
  border-radius: 0.25rem;
  border: 1px solid var(--color-base-grey-200);
  padding: 1px 0.25rem;
  position: absolute;
  transform: translateX(-50%);
}
.ds-range-slider.error .range-info,
.ds-range-slider .range-info .error {
  color: var(--color-error-400);
}
.ds-range-slider.error .range-info > *,
.ds-range-slider .range-info .error > * {
  border: 1px solid var(--color-error-400);
}
.ds-range-slider .text-after {
  grid-column: 1;
  grid-row: 3;
}
</style>
